import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { makeLangUrl } from "helpers/makeLangUrl";

// Actions
import { searchGamesChange, setPage, setSearchResult } from "redux/game/slice";

// Api
import { gamesApi } from "redux/game/api";

// Components
import GameList from "components/organisms/GameList";

// Hooks
import { useBreakpoint } from "hooks/useBreakpoint";
import { useLoaderStore } from "stores/loader";
import { useDebounce } from "hooks/useDebounce";

// Selectors
import * as gameSelectors from "redux/game/selectors";

// Types
import { GAME_PLATFORM, ParamsRequestType } from "types/Games";

// Url
import url from "url/index";

const GameSearchListContainer = () => {
  const games = useSelector(gameSelectors.searchResult);
  const { take, page } = useSelector(gameSelectors.filter);
  const lastPage = useSelector(gameSelectors.lastPage);
  const navigate = useNavigate();
  const location = useLocation();
  const { overlayLoading } = useLoaderStore();
  const { isDesktop } = useBreakpoint();

  const dispatch = useDispatch();

  const params: ParamsRequestType = {
    take,
    page,
    platform: isDesktop ? GAME_PLATFORM.WEB : GAME_PLATFORM.MOB,
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const [trigger, { isLoading }] = gamesApi.useLazyGamesSearchQuery();

  const { watch } = useFormContext();

  const searchQuery = watch("search");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    dispatch(searchGamesChange(debouncedSearchQuery));
    if (debouncedSearchQuery) {
      trigger(
        {
          ...params,
          name: debouncedSearchQuery,
          page: 1,
        },
        false,
      );

      if (location.pathname === url.config.casino.root && !location.search) {
        navigate(makeLangUrl(url.routes.casino.index()));
        dispatch(setPage(1));
      }
    } else {
      dispatch(setSearchResult([]));
    }
    return () => {
      dispatch(searchGamesChange(""));
      trigger(
        {
          ...params,
          name: "",
          page: 1,
        },
        false,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const handleShowMore = () => {
    const nextPage = params.page ? params.page + 1 : 1;
    dispatch(setPage(nextPage));
    trigger({ ...params, page: nextPage, name: searchQuery });
  };

  return (
    <GameList
      list={games}
      isAutoLoading={false}
      take={take}
      page={params.page}
      lastPage={lastPage}
      isLoading={isLoading}
      isOverlayLoading={overlayLoading}
      onShowMore={handleShowMore}
    />
  );
};

export default GameSearchListContainer;
